/*
 * Settings Page - Schema Management
 * 
 * Features:
 * - View and edit schema definitions for accounts and contacts
 * - Add/remove/reorder fields using AG Grid editor
 * - Field name validation to prevent duplicates within schema
 * - Support for field constraints (date validations, conditional requirements, etc.)
 * - Kanban-style collapsible schema navigation with a responsive full-height editor
 * 
 * Implementation:
 * - Field names must be unique within the same schema (no duplicates)
 * - Validation occurs before save
 * - Clear error messages guide users to rename conflicting fields
 * 
 * Changes:
 * - 2026-09-01: Matches the Kanban navigator heading and removes the redundant grid wrapper surface
 * - 2026-09-01: Matches the Kanban header height, typography, spacing, and responsive layout
 * - 2026-09-01: Places the schema row and column actions together in the page header
 * - 2026-09-01: Aligned the canvas, header, editor shell, and schema states with semantic design roles
 * - 2026-09-01: Adopted the shared page layout and compact responsive schema navigator
 * - 2026-01-28: Added field name validation
 * - 2026-01-31: Removed base schema concept
 */

import React, { useState, useEffect } from 'react';
import { THEME_CLASSES } from '../constants/themeConstants';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { Schema } from '../context/SchemaContext';
import Grid from '../components/common/Grid';
import { ColDef } from 'ag-grid-community';
import PageLayout from '../components/patterns/PageLayout';
import { Button } from '../components/primitives';
import { BREAKPOINTS } from '../constants/layoutConstants';
import { foundationColorTokens } from '../foundation';

// Example constraint formats for reference:
/*
Example 1: Meeting scheduling
{
  "name": "meetingDate",
  "type": "date",
  "constraints": [
    {
      "operator": "afterNow",
      "message": "Meeting must be scheduled in the future"
    },
    {
      "operator": "beforeField",
      "field": "contractEndDate",
      "message": "Meeting must be before contract end date"
    }
  ]
}

Example 2: Contract start/end dates
{
  "name": "startDate",
  "type": "date",
  "constraints": [
    {
      "operator": "notPast",
      "message": "Contract start date cannot be in the past"
    }
  ]
},
{
  "name": "endDate",
  "type": "date",
  "constraints": [
    {
      "operator": "afterField",
      "field": "startDate",
      "message": "End date must be after start date"
    }
  ]
}

Example 3: Password confirmation
{
  "name": "confirmPassword",
  "type": "password",
  "constraints": [
    {
      "operator": "equalsField",
      "field": "password",
      "message": "Passwords must match"
    }
  ]
}

Example 4: Conditional business fields
{
  "name": "businessPhone",
  "type": "tel",
  "constraints": [
    {
      "operator": "requiredIf",
      "field": "accountType",
      "value": "business",
      "message": "Business phone is required for business accounts"
    }
  ]
}

Example 5: Follow-up scheduling
{
  "name": "nextOutreach",
  "type": "date",
  "constraints": [
    {
      "operator": "afterField",
      "field": "lastOutreach",
      "message": "Next outreach must be after last outreach"
    },
    {
      "operator": "beforeNow",
      "message": "Next outreach cannot be in the future"
    }
  ]
}

Example 6: Event dates
{
  "name": "eventDate",
  "type": "date",
  "constraints": [
    {
      "operator": "afterNow",
      "message": "Event must be in the future"
    },
    {
      "operator": "beforeField",
      "field": "registrationDeadline",
      "message": "Event must be after registration deadline"
    }
  ]
}
*/

// Local storage key for active schema
const ACTIVE_SCHEMA_KEY = 'rlpCRM-activeSchema';

const Settings: React.FC<{}> = (): JSX.Element => {
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [activeSchema, setActiveSchema] = useState<Schema | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gridData, setGridData] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ message: string, isError: boolean } | null>(null);
  const [isSchemaNavigatorCollapsed, setIsSchemaNavigatorCollapsed] = useState(true);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => (
    typeof window === 'undefined' || window.matchMedia(BREAKPOINTS.desktop).matches
  ));
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    contacts: true,
    accounts: true
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(BREAKPOINTS.desktop);
    const handleMediaChange = (event: MediaQueryListEvent) => setIsDesktopLayout(event.matches);
    setIsDesktopLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    if (!isDesktopLayout) {
      setIsSchemaNavigatorCollapsed(true);
    }
  }, [isDesktopLayout]);

  // Static column definitions based on FormField structure
  const formFieldColDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Field Name',
      width: 250,
      rowDrag: true
    },
    {
      field: 'label',
      headerName: 'Display Label',
      width: 200,
    },
    {
      field: 'type',
      headerName: 'Field Type',
      width: 150,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['text', 'date', 'number', 'boolean', 'textarea', 'select', 'email', 'password', 'tel', 'url']
      }
    },
    {
      field: 'required',
      headerName: 'Required',
      width: 100,
      type: 'boolean',
      cellEditor: 'agCheckboxCellEditor',
      cellRenderer: 'agCheckboxCellRenderer'
    },
    {
      field: 'placeholder',
      headerName: 'Placeholder',
      width: 200
    },
    {
      field: 'options',
      headerName: 'Options',
      type: 'textarea',
      width: 200,
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        // Handle both string and array cases
        let options = Array.isArray(params.value) ? params.value : [];
        try {
          // If it's a string, try to parse it
          if (typeof params.value === 'string') {
            options = JSON.parse(params.value);
          }
        } catch (e) {
          return 'Invalid JSON';
        }
        return options.map((opt: any) => opt.label).join(', ');
      },
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      valueGetter: (params) => {
        if (!params.data?.options) return '[]';
        if (typeof params.data.options === 'string') {
          return params.data.options;
        }
        return JSON.stringify(params.data.options, null, 2);
      },
      valueSetter: (params) => {
        try {
          // Parse the input string as JSON and validate it's an array
          const parsed = JSON.parse(params.newValue || '[]');
          if (!Array.isArray(parsed)) {
            throw new Error('Options must be an array');
          }
          // Validate each option has required properties
          // parsed.forEach((opt: any) => {
          //   if (!opt.label || !opt.value) {
          //     throw new Error('Each option must have label and value properties');
          //   }
          // });
          params.data[params.colDef.field!] = parsed;
          return true;
        } catch (err: any) {
          alert(`Invalid JSON format. Expected array of objects with label and value. Error: ${err.message}`);
          return false;
        }
      },
      cellEditorParams: {
        maxLength: 5000,
        rows: 10,
        cols: 50
      }
    },
    {
      field: 'constraints',
      headerName: 'Constraints',
      width: 300,
      cellRenderer: (params: any) => {
        if (!params.value) return '';
        let constraints = Array.isArray(params.value) ? params.value : [];
        try {
          if (typeof params.value === 'string') {
            constraints = JSON.parse(params.value);
          }
        } catch (e) {
          return 'Invalid JSON';
        }
        return constraints.map((c: any) => {
          let text = `${c.operator}`;
          if (c.field) text += ` -> ${c.field}`;
          if (c.value) text += ` = ${c.value}`;
          return text;
        }).join(', ');
      },
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      valueGetter: (params) => {
        if (!params.data?.constraints) return '[]';
        if (typeof params.data.constraints === 'string') {
          return params.data.constraints;
        }
        return JSON.stringify(params.data.constraints, null, 2);
      },
      valueSetter: (params) => {
        try {
          const parsed = JSON.parse(params.newValue || '[]');
          if (!Array.isArray(parsed)) {
            throw new Error('Constraints must be an array');
          }
          parsed.forEach((constraint: any) => {
            if (!constraint.operator || !constraint.message) {
              throw new Error('Each constraint must have operator and message properties');
            }
            const validOperators = ['afterField', 'beforeField', 'afterNow', 'beforeNow', 'equalsField', 'notFuture', 'notPast', 'requiredIf'];
            if (!validOperators.includes(constraint.operator)) {
              throw new Error(`Invalid operator. Valid operators are: ${validOperators.join(', ')}`);
            }
            if (['afterField', 'beforeField', 'equalsField'].includes(constraint.operator) && !constraint.field) {
              throw new Error(`${constraint.operator} requires a field property`);
            }
            if (constraint.operator === 'requiredIf' && constraint.value === undefined) {
              throw new Error('requiredIf operator requires a value property');
            }
          });
          params.data[params.colDef.field!] = parsed;
          return true;
        } catch (err: any) {
          alert(`Invalid constraints format. Error: ${err.message}`);
          return false;
        }
      },
      cellEditorParams: {
        maxLength: 5000,
        rows: 10,
        cols: 50
      }
    }
  ];

  // Fetch schemas on component mount
  useEffect(() => {
    const fetchSchemas = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await request(API_ENDPOINTS.GET_SCHEMA);

        // Parse schema strings into field objects
        const parsedSchemas = response.map((schema: any) => ({
          ...schema,
          fields: schema.schema ? JSON.parse(schema.schema) : []
        }));

        setSchemas(parsedSchemas);

        // Get previously selected schema from local storage
        const savedSchemaName = localStorage.getItem(ACTIVE_SCHEMA_KEY);

        if (parsedSchemas.length > 0) {
          // Try to find the saved schema
          const savedSchema = savedSchemaName
            ? parsedSchemas.find((schema: Schema) => schema.name === savedSchemaName)
            : null;

          // Use saved schema if found, otherwise use the first one
          const schemaToActivate = savedSchema || parsedSchemas[0];
          setActiveSchema(schemaToActivate);
          setGridData(schemaToActivate.fields || []);
        }
      } catch (err) {
        console.error('Error fetching schemas:', err);
        setError('Failed to load schemas. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchemas();
  }, []);

  // Save selected schema to local storage when it changes
  useEffect(() => {
    if (activeSchema) {
      localStorage.setItem(ACTIVE_SCHEMA_KEY, activeSchema.name);
    }
  }, [activeSchema]);

  // Helper to safely get field count from schema
  const getSchemaFieldCount = (schemaJson: string | null | undefined): number => {
    if (!schemaJson) return 0;
    try {
      return JSON.parse(schemaJson).length;
    } catch (e) {
      console.error('Error parsing schema JSON:', e);
      return 0;
    }
  };

  // Validate field names within the same schema (check for duplicates)
  const validateFieldNames = (fields: any[]): { valid: boolean; conflicts: string[] } => {
    const fieldNames = fields.map(f => f.name).filter(Boolean);
    const seen = new Set<string>();
    const duplicates: string[] = [];

    fieldNames.forEach(name => {
      if (seen.has(name)) {
        if (!duplicates.includes(name)) {
          duplicates.push(name);
        }
      } else {
        seen.add(name);
      }
    });

    return {
      valid: duplicates.length === 0,
      conflicts: duplicates
    };
  };

  const handleValueChanged = async (newData: any[]) => {
    if (!activeSchema) return;

    // Update grid data
    setGridData(newData);

    // Clear previous status
    setSaveStatus(null);

    // Validate field names are unique within this schema
    const validation = validateFieldNames(newData);
    if (!validation.valid) {
      setSaveStatus({
        message: `Cannot save: Duplicate field names found: ${validation.conflicts.join(', ')}. Each field name must be unique.`,
        isError: true
      });
      setIsSaving(false);

      // Keep error message visible longer for validation errors
      setTimeout(() => {
        setSaveStatus(null);
      }, 8000);

      return;
    }

    try {
      setIsSaving(true);

      // Create updated schema object with stringified fields
      const updatedSchema = {
        name: activeSchema.name,
        schema: JSON.stringify(newData)
      };

      // Call the PATCH API to update the schema
      await request(API_ENDPOINTS.PATCH_SCHEMA, updatedSchema);

      // Update local state with the new schema
      const newSchema = {
        ...activeSchema,
        schema: JSON.stringify(newData),
        fields: newData
      };

      setSchemas(prevSchemas =>
        prevSchemas.map(schema =>
          schema.name === activeSchema.name ? newSchema : schema
        )
      );

      // Show success message
      setSaveStatus({
        message: `Schema ${activeSchema.name} updated successfully`,
        isError: false
      });

      // Update active schema
      setActiveSchema(newSchema);

    } catch (err) {
      console.error('Error updating schema:', err);
      setSaveStatus({
        message: 'Failed to update schema. Please try again.',
        isError: true
      });
    } finally {
      setIsSaving(false);

      // Clear status message after a few seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }
  };

  // Render settings content based on active schema
  const renderSettingsContent = () => {
    return (
      <div className="h-full min-h-0 min-w-0">
        {isLoading ? (
          <div className={`flex h-full min-h-64 items-center justify-center rounded-xl border p-6 ${THEME_CLASSES.card.background} ${THEME_CLASSES.card.border}`}>
            <p className={THEME_CLASSES.text.secondary}>Loading schemas...</p>
          </div>
        ) : error ? (
          <div className={`flex h-full min-h-64 items-center justify-center rounded-xl border p-6 text-center ${THEME_CLASSES.card.background} ${THEME_CLASSES.card.border}`}>
            <div>
              <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>Unable to load schemas</h2>
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        ) : activeSchema ? (
          <div
            data-testid="settings-grid-pane"
            className="h-full min-h-0 min-w-0 overflow-hidden"
          >
            <Grid
              name={`schema-editor-${activeSchema.id}`}
              message={saveStatus ? { text: saveStatus.message, isError: saveStatus.isError } : undefined}
              isSaving={isSaving}
              data={gridData}
              columnDefs={formFieldColDefs}
              getRowId={(data) => data.name}
              onValueChanged={handleValueChanged}
              enableRowDrag={true}
              canAddRows={true}
              canRemoveRows={true}
              toolbarPortalTargetId="settings-grid-actions"
              gridOptions={{
                rowSelection: {
                  mode: 'multiRow',
                  checkboxes: true,
                  headerCheckbox: true
                },
                selectionColumnDef: {
                  width: 50,
                  pinned: 'left',
                  // Additional column properties as needed
                },
                rowDragManaged: true,
                animateRows: true
              }}
            />
          </div>
        ) : (
          <div className={`flex h-full min-h-64 items-center justify-center rounded-xl border p-6 text-center ${THEME_CLASSES.card.background} ${THEME_CLASSES.card.border}`}>
            <div>
              <h2 className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>No schemas available</h2>
              <p className={`mt-2 text-sm ${THEME_CLASSES.text.secondary}`}>Schema definitions will appear here when they are available.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`flex h-full min-h-0 overflow-hidden ${THEME_CLASSES.background}`} data-testid="settings-page-root">
      <aside
        data-testid="settings-schema-navigator"
        aria-label="Schema navigator"
        className={`flex shrink-0 flex-col overflow-hidden border-r transition-[width] duration-300 ease-in-out ${isSchemaNavigatorCollapsed ? 'w-12' : 'w-64'} ${THEME_CLASSES.card.background} ${THEME_CLASSES.card.border}`}
      >
        {isSchemaNavigatorCollapsed ? (
          <div className="p-2">
            <Button
              type="button"
              variant="icon"
              className="h-8 w-8"
              onClick={() => setIsSchemaNavigatorCollapsed(false)}
              aria-label="Expand schema navigator"
              aria-expanded="false"
            >
              <svg className={`h-5 w-5 rotate-180 ${THEME_CLASSES.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
          </div>
        ) : (
          <>
            <div id="settings-schema-navigator-content" className="flex min-h-0 flex-1 flex-col">
              <div className={`flex items-center justify-between border-b p-4 ${THEME_CLASSES.card.border}`}>
                <h2 className={`text-md font-semibold ${THEME_CLASSES.text.primary}`}>Schemas</h2>
                <Button
                  type="button"
                  variant="icon"
                  className="h-8 w-8"
                  onClick={() => setIsSchemaNavigatorCollapsed(true)}
                  aria-label="Collapse schema navigator"
                  aria-expanded="true"
                >
                  <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
              </div>

              {schemas.length > 0 && (
                <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div>
                  <button
                    type="button"
                    onClick={() => toggleSection('accounts')}
                    aria-expanded={expandedSections.accounts}
                    aria-controls="settings-account-schemas"
                    className={`flex w-full items-center justify-between rounded-lg border p-3 ${THEME_CLASSES.dropdown.background} ${THEME_CLASSES.card.border} ${THEME_CLASSES.hover.background}`}
                  >
                    <span className={`text-sm font-semibold uppercase tracking-wide ${THEME_CLASSES.dropdown.item.base}`}>Accounts</span>
                    <svg className={`h-5 w-5 transition-transform ${expandedSections.accounts ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <ul id="settings-account-schemas" hidden={!expandedSections.accounts} className="mt-2 space-y-1">
                    {schemas.filter(schema => schema.entityType === 2).map((schema) => (
                      <li key={schema.name}>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSchema(schema);
                            setGridData(schema.schema ? JSON.parse(schema.schema) : []);
                          }}
                          aria-label={`${schema.name}, ${getSchemaFieldCount(schema.schema)} ${getSchemaFieldCount(schema.schema) === 1 ? 'field' : 'fields'}`}
                          aria-pressed={activeSchema?.name === schema.name}
                          className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${activeSchema?.name === schema.name
                            ? THEME_CLASSES.button.primary
                            : `${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`
                            }`}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate">{schema.name}</span>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${activeSchema?.name === schema.name
                              ? 'bg-white/20 text-white'
                              : `${foundationColorTokens.background.surfaceMuted} ${THEME_CLASSES.text.secondary}`
                              }`}>
                              {getSchemaFieldCount(schema.schema)}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => toggleSection('contacts')}
                    aria-expanded={expandedSections.contacts}
                    aria-controls="settings-contact-schemas"
                    className={`flex w-full items-center justify-between rounded-lg border p-3 ${THEME_CLASSES.dropdown.background} ${THEME_CLASSES.card.border} ${THEME_CLASSES.hover.background}`}
                  >
                    <span className={`text-sm font-semibold uppercase tracking-wide ${THEME_CLASSES.dropdown.item.base}`}>Contacts</span>
                    <svg className={`h-5 w-5 transition-transform ${expandedSections.contacts ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <ul id="settings-contact-schemas" hidden={!expandedSections.contacts} className="mt-2 space-y-1">
                    {schemas.filter(schema => schema.entityType === 1).map((schema) => (
                      <li key={schema.name}>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSchema(schema);
                            setGridData(schema.schema ? JSON.parse(schema.schema) : []);
                          }}
                          aria-label={`${schema.name}, ${getSchemaFieldCount(schema.schema)} ${getSchemaFieldCount(schema.schema) === 1 ? 'field' : 'fields'}`}
                          aria-pressed={activeSchema?.name === schema.name}
                          className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${activeSchema?.name === schema.name
                            ? THEME_CLASSES.button.primary
                            : `${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`
                            }`}
                        >
                          <span className="flex items-center justify-between gap-2">
                            <span className="truncate">{schema.name}</span>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${activeSchema?.name === schema.name
                              ? 'bg-white/20 text-white'
                              : `${foundationColorTokens.background.surfaceMuted} ${THEME_CLASSES.text.secondary}`
                              }`}>
                              {getSchemaFieldCount(schema.schema)}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                </div>
              )}
            </div>
          </>
        )}
      </aside>

      <PageLayout className="min-w-0 flex-1">
        <header data-testid="settings-page-header">
          <div
            className="flex flex-col gap-4 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between"
            data-testid="settings-page-header-layout"
          >
            <div className="flex min-h-8 flex-wrap items-center gap-3" data-testid="settings-page-title-row">
              <h1 className={`my-0 text-xl font-bold ${THEME_CLASSES.text.primary}`}>Settings</h1>
            </div>
            <div
              id="settings-grid-actions"
              className="flex h-9 max-w-full items-center overflow-x-auto [scrollbar-width:none] sm:h-[34px] [&::-webkit-scrollbar]:hidden"
              role="group"
              aria-label="Schema actions"
            />
          </div>
        </header>

        <div data-testid="settings-content" className="min-h-0 min-w-0 flex-1 overflow-hidden px-4 pb-4 sm:px-6 sm:pb-6">
          {renderSettingsContent()}
        </div>
      </PageLayout>
    </div>
  );
};

export default Settings;
